import React, { createContext, useContext, useMemo } from "react";

import { serviceProfileApi } from "@/services/profile/index";
import { serviceProductsApi } from "@/services/products/index";
import { serviceMenuApi } from "@/services/menu/index";
import { serviceNotificationsApi } from "@/services/notifications/index";
import { serviceMarketplaceCartApi } from "@/services/marketplace-cart/index";
import { serviceLocalizationApi } from "@/services/localization/index";
import { serviceDeploymentApi } from "@/services/deployment/index";
import { serviceScanDataApi } from "@/services/scan-data/index";
import { serviceShippingApi } from "@/services/shipping/index";
import { serviceTrackingApi } from "@/services/tracking/index";
import { serviceOnboardingApi } from "@/services/onboarding/index";
import { serviceSystemViewApi } from "@/services/system-view/index";
import { serviceMasterLicenseAgreementApi } from "@/services/master-license-agreement/index";
import { serviceAccountsApi } from "@/services/accounts/index";
import { serviceBusinessUnitsApi } from "@/services/business-units/index";
import { serviceEmployeesApi } from "@/services/employees/index";
import { serviceTaxonomyApi } from "@/services/taxonomy/index";
import { serviceSearchApi } from "@/services/search/index";
import { serviceSupportCasesApi } from "@/services/support-cases/index";
import { serviceKnowledgeBaseApi } from "@/services/knowledge-base/index";
import { serviceBillingApi } from "@/services/billing/index";
import { serviceServicesApi } from "@/services/services/index";
import { serviceFeedbackApi } from "@/services/feedback/index";
import { serviceHardwareApi } from "@/services/hardware/index";
import { serviceSubscriptionsApi } from "@/services/subscriptions/index";
import { serviceTemporaryApi } from "@/services/temporary/index";

import type { ServiceProfileApiType } from "@/services/profile/profile-api";
import type { ServiceProductsApiType } from "@/services/products/products-api";
import type { ServiceMenuApiType } from "@/services/menu/menu-api";
import type { ServiceNotificationsApiType } from "@/services/notifications/notifications-api";
import type { ServiceMarketplaceCartApiType } from "@/services/marketplace-cart/marketplace-cart-api";
import type { ServiceLocalizationApiType } from "@/services/localization/localization-api";
import type { ServiceDeploymentApi } from "@/services/deployment/deployment-api";
import type { ServiceScanDataApi } from "@/services/scan-data/scan-data-api";
import type { ServiceShippingApi } from "@/services/shipping/shipping-api";
import type { ServiceTrackingApi } from "@/services/tracking/tracking-api";
import type { ServiceOnboardingApiType } from "@/services/onboarding/onboarding-api";
import type { ServiceSystemViewApi } from "@/services/system-view/system-view-api";
import type { ServiceMasterLicenseAgreementApiType } from "@/services/master-license-agreement/master-license-agreement-api";
import type { ServiceAccountsApiType } from "@/services/accounts/accounts-api";
import type { ServiceBusinessUnitsApiType } from "@/services/business-units/business-units-api";
import type { ServiceEmployeesApiType } from "@/services/employees/employees-api";
import type { ServiceTaxonomyApiType } from "@/services/taxonomy/taxonomy-api";
import type { ServiceSearchApiType } from "@/services/search/search-api";
import type { ServiceSupportCasesApiType } from "@/services/support-cases/support-cases-api";
import type { ServiceKnowledgeBaseApiType } from "@/services/knowledge-base/knowledge-base-api";
import type { ServiceBillingApiType } from "@/services/billing/billing-api";
import type { ServiceServicesApi } from "@/services/services/services-api";
import type { ServiceFeedbackApiType } from "@/services/feedback/index";
import type { ServiceHardwareApiType } from "@/services/hardware/hardware-api";
import type { ServiceSubscriptionsApiType } from "@/services/subscriptions/subscriptions-api";
import type { ServiceTemporaryApiType } from "@/services/temporary/temporary-api";

export interface AllServicesApi {
  profile: ServiceProfileApiType;
  products: ServiceProductsApiType;
  menu: ServiceMenuApiType;
  notifications: ServiceNotificationsApiType;
  marketplaceCart: ServiceMarketplaceCartApiType;
  localization: ServiceLocalizationApiType;
  deployment: ServiceDeploymentApi;
  scanData: ServiceScanDataApi;
  shipping: ServiceShippingApi;
  tracking: ServiceTrackingApi;
  onboarding: ServiceOnboardingApiType;
  systemView: ServiceSystemViewApi;
  masterLicenseAgreement: ServiceMasterLicenseAgreementApiType;
  accounts: ServiceAccountsApiType;
  businessUnits: ServiceBusinessUnitsApiType;
  employees: ServiceEmployeesApiType;
  taxonomy: ServiceTaxonomyApiType;
  search: ServiceSearchApiType;
  supportCases: ServiceSupportCasesApiType;
  knowledgeBase: ServiceKnowledgeBaseApiType;
  billing: ServiceBillingApiType;
  services: ServiceServicesApi;
  feedback: ServiceFeedbackApiType;
  hardware: ServiceHardwareApiType;
  subscriptions: ServiceSubscriptionsApiType;
  temporary: ServiceTemporaryApiType;
}

const ServicesContext = createContext<AllServicesApi | null>(null);

export function ServicesProvider({ children }: { children: React.ReactNode }) {
  const services = useMemo(
    () => ({
      profile: serviceProfileApi,
      products: serviceProductsApi,
      menu: serviceMenuApi,
      notifications: serviceNotificationsApi,
      marketplaceCart: serviceMarketplaceCartApi,
      localization: serviceLocalizationApi,
      deployment: serviceDeploymentApi,
      scanData: serviceScanDataApi,
      shipping: serviceShippingApi,
      tracking: serviceTrackingApi,
      onboarding: serviceOnboardingApi,
      systemView: serviceSystemViewApi,
      masterLicenseAgreement: serviceMasterLicenseAgreementApi,
      accounts: serviceAccountsApi,
      businessUnits: serviceBusinessUnitsApi,
      employees: serviceEmployeesApi,
      taxonomy: serviceTaxonomyApi,
      search: serviceSearchApi,
      supportCases: serviceSupportCasesApi,
      knowledgeBase: serviceKnowledgeBaseApi,
      billing: serviceBillingApi,
      services: serviceServicesApi,
      feedback: serviceFeedbackApi,
      hardware: serviceHardwareApi,
      subscriptions: serviceSubscriptionsApi,
      temporary: serviceTemporaryApi,
    }),
    [],
  );

  return (
    <ServicesContext.Provider value={services}>
      {children}
    </ServicesContext.Provider>
  );
}

export function useAllServices() {
  const context = useContext(ServicesContext);
  if (!context) {
    throw new Error(
      "useAllServices must be used within CompositeServicesProvider",
    );
  }
  return context;
}

export function useProfileService() {
  const { profile } = useAllServices();
  return profile;
}

export function useProductsService() {
  const { products } = useAllServices();
  return products;
}

export function useMenuService() {
  const { menu } = useAllServices();
  return menu;
}

export function useNotificationsService() {
  const { notifications } = useAllServices();
  return notifications;
}

export function useMarketplaceCartService() {
  const { marketplaceCart } = useAllServices();
  return marketplaceCart;
}

export function useLocalizationService() {
  const { localization } = useAllServices();
  return localization;
}

export function useDeploymentService() {
  const { deployment } = useAllServices();
  return deployment;
}

export function useScanDataService() {
  const { scanData } = useAllServices();
  return scanData;
}

export function useShippingService() {
  const { shipping } = useAllServices();
  return shipping;
}

export function useTrackingService() {
  const { tracking } = useAllServices();
  return tracking;
}

export function useOnboardingService() {
  const { onboarding } = useAllServices();
  return onboarding;
}

export function useSystemViewService() {
  const { systemView } = useAllServices();
  return systemView;
}

export function useMasterLicenseAgreementService() {
  const { masterLicenseAgreement } = useAllServices();
  return masterLicenseAgreement;
}

export function useAccountsService() {
  const { accounts } = useAllServices();
  return accounts;
}

export function useBusinessUnitsService() {
  const { businessUnits } = useAllServices();
  return businessUnits;
}

export function useEmployeesService() {
  const { employees } = useAllServices();
  return employees;
}

export function useTaxonomyService() {
  const { taxonomy } = useAllServices();
  return taxonomy;
}

export function useSearchService() {
  const { search } = useAllServices();
  return search;
}

export function useSupportCasesService() {
  const { supportCases } = useAllServices();
  return supportCases;
}

export function useKnowledgeBaseService() {
  const { knowledgeBase } = useAllServices();
  return knowledgeBase;
}

export function useBillingService() {
  const { billing } = useAllServices();
  return billing;
}

export function useServicesService() {
  const { services } = useAllServices();
  return services;
}

export function useFeedbackService() {
  const { feedback } = useAllServices();
  return feedback;
}

export function useHardwareService() {
  const { hardware } = useAllServices();
  return hardware;
}

export function useSubscriptionsService() {
  const { subscriptions } = useAllServices();
  return subscriptions;
}

export function useTemporaryService() {
  const { temporary } = useAllServices();
  return temporary;
}
